import { FC, useEffect, useMemo, useState } from "react";
import Modal from "../Modal";
import { StoreShipmentInterface } from "../../interfaces/Dtos";
import { getDocumentCompatibleShipments } from "../../services";
import { FormCheckbox, FormTextInput } from "../FormFields";
import { PrimaryButton, SecondaryButton } from "../Buttons";
import classNames from "classnames";
import { formatName } from "../../utils";
import PaginationFooter from "../PaginationFooter";
import LoadingIcon from "../LodingIcon";

const ROWS_PER_PAGE = 5;

export interface DocumentCompatibleShipmentsModalProps {
  shipmentNumber: string;
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  onSelect: (shipmentNumbers: string[]) => void;
}
const DocumentCompatibleShipmentsModal: FC<
  DocumentCompatibleShipmentsModalProps
> = ({ shipmentNumber, openModal, setOpenModal, onSelect }) => {
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [itemOffset, setItemOffset] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedShipments, setSelectedShipments] = useState([shipmentNumber]);
  const [compatibleShipments, setCompatibleShipments] = useState<
    StoreShipmentInterface[]
  >([]);

  const filteredCompatibleShipments = useMemo(() => {
    return compatibleShipments
      .filter(
        (s) =>
          s.shipmentNumber.indexOf(filter) !== -1 ||
          s.shipmentNumber === shipmentNumber
      )
      .sort((a, b) => {
        if (a.shipmentNumber === shipmentNumber) {
          return -1;
        } else if (b.shipmentNumber === shipmentNumber) {
          return 1;
        }
        return 0;
      });
  }, [compatibleShipments, filter, shipmentNumber]);

  const paginatedCompatibleShipments = useMemo(() => {
    return filteredCompatibleShipments.slice(
      itemOffset,
      itemOffset + ROWS_PER_PAGE
    );
  }, [filteredCompatibleShipments, itemOffset]);

  const allSelected = useMemo(() => {
    return filteredCompatibleShipments.every((shipment) =>
      selectedShipments.some((s) => s === shipment.shipmentNumber)
    );
  }, [filteredCompatibleShipments, selectedShipments]);

  const handlePageClick = (event: { selected: number }) => {
    const newOffset =
      (event.selected * ROWS_PER_PAGE) % filteredCompatibleShipments.length;
    setItemOffset(newOffset);
  };

  const handleSelectAll = () => {
    if (selectedShipments.length === filteredCompatibleShipments.length) {
      setSelectedShipments([shipmentNumber]);
    } else {
      setSelectedShipments(
        filteredCompatibleShipments.map((s) => s.shipmentNumber)
      );
    }
  };

  const handleBoxSelect = (
    shipment: StoreShipmentInterface,
    selected: boolean
  ) => {
    if (shipment.shipmentNumber === shipmentNumber) {
      return;
    }

    if (selected) {
      setSelectedShipments(
        selectedShipments.filter((n) => n !== shipment.shipmentNumber)
      );
    } else {
      setSelectedShipments([...selectedShipments, shipment.shipmentNumber]);
    }
  };

  useEffect(() => {
    if (!openModal) {
      return;
    }

    setSelectedShipments([shipmentNumber]);

    const afunction = async () => {
      setLoading(true);
      const response = await getDocumentCompatibleShipments(shipmentNumber);

      if (response.didError || !response.model) {
        setCompatibleShipments([]);
        setLoading(false);
      } else if (response.model.length === 1) {
        onSelect([shipmentNumber]);
      } else {
        setCompatibleShipments(response.model);
        setLoading(false);
      }
    };

    afunction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentNumber, openModal]);

  useEffect(() => {
    setTotalPages(
      Math.ceil(filteredCompatibleShipments.length / ROWS_PER_PAGE)
    );
  }, [itemOffset, filteredCompatibleShipments]);

  return (
    <Modal
      openModal={openModal}
      setOpenModal={setOpenModal}
      className="bg-gray-200"
    >
      <div className={classNames("flex flex-col gap-6", loading && "hidden")}>
        <div>
          <p className="text-lg font-semibold">
            Si quiere procesar otras guías en la misma orden junto a la{" "}
            <span className="text-indigo-600">{shipmentNumber}</span>{" "}
            selecciónelas aquí
          </p>

          <p className="text-gray-800">
            Sólo se muestran aquellas guías que aplican para ser procesadas en
            la misma orden
          </p>
        </div>

        <div>
          <FormTextInput
            name="document-compatible-shipments-filter"
            label="Buscar guías"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>

        <div className="">
          <table className="table-auto w-full">
            <thead>
              <tr>
                <th className="text-left px-4 py-2 font-bold text-gray-700 text-xs w-5">
                  <FormCheckbox
                    id={`select-all`}
                    name={`select-all`}
                    label=""
                    onChange={handleSelectAll}
                    checked={allSelected}
                  />
                </th>

                <th className="text-left px-4 py-2 font-semibold text-xs">
                  GUÍA
                </th>

                <th className="text-left px-4 py-2 font-semibold text-xs">
                  REMITENTE
                </th>

                <th className="text-left px-4 py-2 font-semibold text-xs">
                  ORIGEN
                </th>

                <th className="text-left px-4 py-2 font-semibold text-xs">
                  DESTINO
                </th>

                <th className="text-left px-4 py-2 font-semibold text-xs">
                  DESTINATARIO
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedCompatibleShipments.map((shipment, index) => {
                const selected = selectedShipments.some(
                  (s) => s === shipment.shipmentNumber
                );

                return (
                  <tr
                    key={index}
                    className={classNames(
                      "hover:bg-gray-100",
                      index % 2 === 1 && "bg-gray-50"
                    )}
                  >
                    <td className="text-xs px-4 py-4 flex items-end justify-end">
                      <FormCheckbox
                        id={`select-${shipment.shipmentNumber}-${index}`}
                        name={`select-${shipment.shipmentNumber}-${index}`}
                        label=""
                        onChange={() => handleBoxSelect(shipment, selected)}
                        checked={selected}
                      />
                    </td>

                    <td className="text-xs px-4 py-4 font-bold text-gray-500 truncate text-left">
                      {shipment.shipmentNumber}
                    </td>

                    <td className="text-xs px-4 py-4 font-bold text-gray-500 truncate text-left truncate max-w-[15rem]">
                      {formatName(shipment.shipperName)}
                    </td>

                    <td className="text-xs px-4 py-4 font-semibold text-gray-500 truncate max-w-[15rem]">
                      {shipment.buCodeSource} - {shipment.sourceBUAreaName}
                    </td>

                    <td className="text-xs px-4 py-4 font-semibold text-gray-500 truncate max-w-[15rem]">
                      {shipment.buCodeConsignee} -{" "}
                      {shipment.consigneeBUAreaName}
                    </td>

                    <td className="text-xs px-4 py-4 font-semibold text-gray-500 truncate text-left truncate max-w-[15rem]">
                      {formatName(shipment.consigneeName)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <PaginationFooter
            rowCounts={ROWS_PER_PAGE}
            itemsOffSet={itemOffset}
            totalItems={filteredCompatibleShipments.length}
            handlePageClick={handlePageClick}
            totalPages={totalPages}
          />
        </div>

        <div className="flex justify-between gap-6">
          <SecondaryButton className="w-32" onClick={() => setOpenModal(false)}>
            Cancelar
          </SecondaryButton>

          <PrimaryButton
            className="w-32"
            onClick={() => onSelect(selectedShipments)}
          >
            Retomar
          </PrimaryButton>
        </div>
      </div>

      <div
        className={classNames(
          "flex flex-col items-center justify-center p-10",
          !loading && "hidden"
        )}
      >
        <LoadingIcon size="5rem" />
      </div>
    </Modal>
  );
};

export default DocumentCompatibleShipmentsModal;
